import React, { useState } from 'react';
import styled from 'styled-components';

const PageContainer = styled.div`
  padding: 40px 0;
`;

const Container = styled.div.attrs({ className: 'app-container' })`
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
`;

const SectionTitle = styled.h1`
  text-align: center;
  margin-bottom: 40px;
  
  &::after {
    content: '';
    display: block;
    width: 50px;
    height: 3px;
    background-color: var(--accent-color);
    margin: 15px auto 0;
  }
`;

const ContentContainer = styled.div`
  background-color: var(--light-background);
  padding: 30px;
  border-radius: 8px;
  max-width: 800px;
  margin: 0 auto;
`;

const FaqContainer = styled.div`
  margin-top: 30px;
`;

const FaqItem = styled.div`
  margin-bottom: 20px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.1);
  padding-bottom: 20px;
  
  &:last-child {
    border-bottom: none;
    margin-bottom: 0;
  }
`;

const QuestionContainer = styled.div`
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  
  &:hover h3 {
    color: var(--accent-color);
  }
`;

const Question = styled.h3`
  font-size: 1.2rem;
  font-weight: 600;
  transition: color 0.3s ease;
`;

const ToggleIcon = styled.span`
  font-size: 1.2rem;
  color: var(--accent-color);
  transition: transform 0.3s ease;
  transform: ${({ isOpen }) => isOpen ? 'rotate(180deg)' : 'rotate(0)'};
`;

const Answer = styled.div`
  padding-top: ${({ isOpen }) => isOpen ? '15px' : '0'};
  max-height: ${({ isOpen }) => isOpen ? '1000px' : '0'};
  overflow: hidden;
  transition: all 0.3s ease;
  
  p {
    line-height: 1.6;
    margin-bottom: 10px;
  }
  
  ul {
    padding-left: 20px;
    margin-bottom: 10px;
  }
  
  li {
    margin-bottom: 5px;
  }
  
  a {
    color: var(--accent-color);
    text-decoration: none;
    
    &:hover {
      text-decoration: underline;
    }
  }
`;

const SearchContainer = styled.div`
  margin-bottom: 30px;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 1rem;
  
  &:focus {
    outline: none;
    border-color: var(--accent-color);
  }
`;

const CategoryTabs = styled.div`
  display: flex;
  margin-bottom: 30px;
  border-bottom: 1px solid #ddd;
  overflow-x: auto;
  padding-bottom: 5px;
  
  @media (max-width: 768px) {
    justify-content: flex-start;
  }
`;

const CategoryTab = styled.button`
  background: none;
  border: none;
  padding: 10px 15px;
  font-size: 1rem;
  cursor: pointer;
  white-space: nowrap;
  color: ${({ isActive }) => isActive ? 'var(--accent-color)' : 'inherit'};
  font-weight: ${({ isActive }) => isActive ? '600' : '400'};
  border-bottom: ${({ isActive }) => isActive ? '2px solid var(--accent-color)' : 'none'};
  
  &:hover {
    color: var(--accent-color);
  }
`;

const NoResults = styled.div`
  text-align: center;
  padding: 30px 0;
  color: #666;
`;

const FAQPage = () => {
    const [activeCategory, setActiveCategory] = useState('all');
    const [searchQuery, setSearchQuery] = useState('');
    const [openItems, setOpenItems] = useState({});

    const faqData = [
        {
            id: 1,
            category: 'ordering',
            question: 'How do I create and order a star map?',
            answer: `
                <p>Creating and ordering your personalized star map is easy:</p>
                <ol>
                    <li>Go to the "Create" page from our main menu</li>
                    <li>Enter the date, time, and location of your special moment</li>
                    <li>Add a personal caption (optional)</li>
                    <li>Choose your preferred style, size, and frame options</li>
                    <li>Preview your map and make any adjustments</li>
                    <li>Add to cart and proceed to checkout</li>
                </ol>
                <p>After completing your purchase, we'll prepare your custom star map and ship it to your address.</p>
            `
        },
        {
            id: 2,
            category: 'product',
            question: 'What makes InterstellarMap special?',
            answer: `
                <p>InterstellarMap creates scientifically accurate representations of the night sky based on real astronomical data. We use precise celestial coordinates and star catalogs to show you exactly how the stars appeared at your chosen date, time, and location.</p>
                <p>Our maps are not only beautiful keepsakes but also astronomically correct. Each star's position, brightness, and constellation is accurately plotted according to astronomical databases, making your map both meaningful and scientifically fascinating.</p>
            `
        },
        {
            id: 3,
            category: 'shipping',
            question: 'How long does shipping take?',
            answer: `
                <p>Shipping times depend on your location and the shipping method you select at checkout:</p>
                <ul>
                    <li><strong>Standard Shipping:</strong> 5-7 business days within the continental US</li>
                    <li><strong>Express Shipping:</strong> 2-3 business days within the continental US</li>
                    <li><strong>International Shipping:</strong> 10-15 business days, depending on destination</li>
                </ul>
                <p>Please note that all shipping times are estimates and begin after your custom map has been prepared (typically 1-2 business days after ordering).</p>
            `
        },
        {
            id: 4,
            category: 'technical',
            question: 'Why am I seeing a "Using simplified star map" message?',
            answer: `
                <p>If you see this message, it means our application is having difficulty loading the full celestial data library. This can happen for a few reasons:</p>
                <ul>
                    <li>Your internet connection may be slower than usual</li>
                    <li>Your browser might be blocking some scripts needed for the full map</li>
                    <li>There might be temporary issues with our star data server</li>
                </ul>
                <p>Don't worry - our application automatically switches to a simplified star map that still looks beautiful. While it may not have the same level of astronomical accuracy as our full star maps, it will give you a good preview of what your final product will look like.</p>
                <p>The simplified map will not affect your final printed product, which will always be created using our complete astronomical database.</p>
                <p>You can try refreshing the page or returning later to see the full star map.</p>
            `
        },
        {
            id: 5,
            category: 'product',
            question: 'What frame options are available?',
            answer: `
                <p>We offer several framing options to complement your star map:</p>
                <ul>
                    <li><strong>Classic Black:</strong> Elegant matte black frame with glass front</li>
                    <li><strong>Modern White:</strong> Clean white frame with glass front</li>
                    <li><strong>Natural Wood:</strong> Warm wooden frame with glass front</li>
                    <li><strong>Gold Accent:</strong> Premium gold-finished frame with glass front</li>
                    <li><strong>Frameless:</strong> Just the print, perfect if you want to use your own frame</li>
                </ul>
                <p>All frames come with mounting hardware and are ready to hang.</p>
            `
        },
        {
            id: 6,
            category: 'technical',
            question: 'Can I create a map for any location on Earth?',
            answer: `
                <p>Yes! Our star maps can be generated for any location on Earth. Simply enter the city name or specific latitude and longitude coordinates when creating your map.</p>
                <p>The star positions will accurately reflect how the night sky appeared from that exact location on your chosen date and time.</p>
            `
        },
        {
            id: 7,
            category: 'ordering',
            question: 'Can I add a custom message to my star map?',
            answer: `
                <p>Absolutely! During the map creation process, you can add a custom caption that will appear on your star map. This could be:</p>
                <ul>
                    <li>A special date: "The day we met - June 15, 2018"</li>
                    <li>Names: "John & Sarah"</li>
                    <li>A meaningful quote: "Written in the stars"</li>
                    <li>A special message: "The night you came into our world"</li>
                </ul>
                <p>The caption will be elegantly positioned beneath the star map, creating a truly personalized keepsake.</p>
            `
        },
        {
            id: 8,
            category: 'refunds',
            question: 'What is your refund policy?',
            answer: `
                <p>We want you to be completely satisfied with your InterstellarMap:</p>
                <ul>
                    <li>If your map arrives damaged or has printing defects, we'll replace it free of charge</li>
                    <li>If you're not happy with your order for any reason, you can return it within 30 days for a full refund</li>
                    <li>Custom maps with personalized text cannot be refunded unless there's a production error on our end</li>
                </ul>
                <p>To initiate a return or replacement, please contact our customer service team through our <a href="/contact">Contact page</a>.</p>
            `
        },
        {
            id: 9,
            category: 'technical',
            question: 'How accurate are the star positions?',
            answer: `
                <p>Our star maps use scientific astronomical data to create highly accurate representations of the night sky. We use the following sources:</p>
                <ul>
                    <li>The Yale Bright Star Catalog for star positions and magnitudes</li>
                    <li>IAU constellation data for official constellation lines and boundaries</li>
                    <li>Precise astronomical algorithms to calculate star positions based on date, time, and location</li>
                </ul>
                <p>The maps show stars down to magnitude 6.5, which represents approximately what would be visible to the naked eye under good viewing conditions away from city lights.</p>
            `
        },
        {
            id: 10,
            category: 'ordering',
            question: 'Can I order a map as a gift?',
            answer: `
                <p>Yes! Star maps make wonderful personalized gifts. Here's how to make gift-giving easy:</p>
                <ul>
                    <li>You can add a gift message during checkout</li>
                    <li>We offer gift wrapping for an additional fee</li>
                    <li>We can ship directly to the recipient (and won't include pricing information)</li>
                    <li>If you're not sure what date/location to choose, we offer gift cards</li>
                </ul>
                <p>Star maps make perfect gifts for weddings, anniversaries, births, graduations, or any special occasion marked by a specific date and place.</p>
            `
        },
        {
            id: 11,
            category: 'technical',
            question: 'Why does the star map take some time to load initially?',
            answer: `
                <p>When you first visit the Create page, our application needs to download the astronomical data files required to generate scientifically accurate star maps. These files contain:
                </p>
                <ul>
                    <li>Star positions and magnitudes</li>
                    <li>Constellation patterns and boundaries</li>
                    <li>Deep sky objects</li>
                </ul>
                <p>This data is downloaded only once per session and then cached in your browser, so subsequent star map generations will be much faster. This approach ensures the highest level of accuracy without requiring a large initial download of the entire application.</p>
                <p>If your internet connection is slower than usual, you might see the loading progress indicator for a bit longer. In the rare case where the data fails to load, our application will automatically switch to a simplified star map display.</p>
            `
        },
    ];

    const handleToggle = (id) => {
        setOpenItems(prev => ({
            ...prev,
            [id]: !prev[id]
        }));
    };

    const handleSearchChange = (e) => {
        setSearchQuery(e.target.value);
    };

    const filterFAQs = () => {
        return faqData.filter(item => {
            const matchesCategory = activeCategory === 'all' || item.category === activeCategory;
            const matchesSearch = item.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
                item.answer.toLowerCase().includes(searchQuery.toLowerCase());

            return matchesCategory && matchesSearch;
        });
    };

    const categories = [
        { id: 'all', name: 'All Questions' },
        { id: 'ordering', name: 'Ordering' },
        { id: 'product', name: 'Product Information' },
        { id: 'shipping', name: 'Shipping' },
        { id: 'technical', name: 'Technical Help' },
        { id: 'refunds', name: 'Returns & Refunds' }
    ];

    const filteredFAQs = filterFAQs();

    return (
        <PageContainer>
            <Container>
                <SectionTitle>Frequently Asked Questions</SectionTitle>

                <ContentContainer>
                    <SearchContainer>
                        <SearchInput
                            type="text"
                            placeholder="Search questions..."
                            value={searchQuery}
                            onChange={handleSearchChange}
                        />
                    </SearchContainer>

                    <CategoryTabs>
                        {categories.map(category => (
                            <CategoryTab
                                key={category.id}
                                isActive={activeCategory === category.id}
                                onClick={() => setActiveCategory(category.id)}
                            >
                                {category.name}
                            </CategoryTab>
                        ))}
                    </CategoryTabs>

                    <FaqContainer>
                        {filteredFAQs.length > 0 ? (
                            filteredFAQs.map(item => (
                                <FaqItem key={item.id}>
                                    <QuestionContainer onClick={() => handleToggle(item.id)}>
                                        <Question>{item.question}</Question>
                                        <ToggleIcon isOpen={openItems[item.id]}>
                                            ▼
                                        </ToggleIcon>
                                    </QuestionContainer>

                                    <Answer isOpen={openItems[item.id]}
                                        dangerouslySetInnerHTML={{ __html: item.answer }}
                                    />
                                </FaqItem>
                            ))
                        ) : (
                            <NoResults>
                                No FAQs match your search. Try different keywords or categories.
                            </NoResults>
                        )}
                    </FaqContainer>
                </ContentContainer>
            </Container>
        </PageContainer>
    );
};

export default FAQPage; 